import {
  Box,
  Button,
  Card,
  Grid,
  ListContainer,
  ListContext,
  MenuButton,
  NoData,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  formatDecimal,
  useTheme,
} from "@compass/core";
import { Permission, PermissionAction, StudentTranscriptGradeTerm } from "generated/openApi";
import { useRouter } from "hooks";
import "moment-timezone";
import { studentGradeBulkCreatePageRoute } from "pages/students/student-grades/StudentGradeBulkCreatePage/StudentGradeBulkCreatePage";
import { studentGradePageRoute } from "pages/students/student-grades/StudentGradePage/StudentGradePage";
import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";

export const Grades = (props: {
  studentId: number;
  year: number | undefined;
  gradeTerms: StudentTranscriptGradeTerm[];
  onGetList: () => void;
}) => {
  const ROOT_PERMISSION: Permission = Permission.StudentGrade;
  const navigate = useNavigate();
  const theme = useTheme();
  const { matchTemplatePath } = useRouter();

  return (
    <>
      <Card
        fullHeight
        header={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="inherit">Grades</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <MenuButton
                  size="small"
                  label="+ Grade"
                  onClick={() => {
                    navigate({
                      pathname: studentGradePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                      search: studentGradePageRoute.getOptionalParams?.({
                        studentId: props.studentId,
                        schoolYear: props.year,
                      }),
                    });
                  }}
                  options={[
                    {
                      label: "+ Batch Grades",
                      action: () =>
                        navigate({
                          pathname: studentGradeBulkCreatePageRoute.getPath({ studentId: props.studentId }),
                          search: studentGradeBulkCreatePageRoute.getOptionalParams?.({
                            schoolYear: props.year,
                          }),
                        }),
                    },
                  ]}
                />
              )}
            </Stack>
          </Stack>
        }
      >
        <Grid container spacing="md">
          {props.gradeTerms.map((gradeTerm, index) => (
            <Grid key={index} item xs={6}>
              <ListContainer
                type="grid"
                fullHeight
                header={
                  <Stack direction="row" justifyContent="space-between">
                    <Stack>
                      <Typography fontWeight="medium">
                        {gradeTerm.termType} {gradeTerm.termNumber}
                      </Typography>
                      <Typography variant="caption">{gradeTerm.school}</Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing="sm">
                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                        <Button
                          variant="text"
                          onClick={() => {
                            navigate({
                              pathname: studentGradePageRoute.getPath({
                                id: 0,
                                crudMode: "edit",
                                screenType: "dialog",
                              }),
                              search: studentGradePageRoute.getOptionalParams?.({
                                studentId: props.studentId,
                                schoolYear: props.year,
                                schoolId: gradeTerm.schoolId,
                                termTypeId: gradeTerm.termTypeId,
                                termNumber: gradeTerm.termNumber,
                              }),
                            });
                          }}
                          sx={{ minWidth: "auto" }}
                        >
                          + Grade
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                }
                tableContainer={
                  <TableContainer>
                    <Table stickyHeader>
                      {/* <TableHead>
                        <TableRow>
                          <TableCell>Class</TableCell>
                          <TableCell align="right">Grade</TableCell>
                        </TableRow>
                      </TableHead> */}
                      <TableBody>
                        {gradeTerm.grades?.map((grade) => (
                          <TableRow
                            key={grade.id}
                            onClick={() =>
                              navigate({
                                pathname: studentGradePageRoute.getPath({
                                  id: grade.id,
                                  crudMode: "view",
                                  screenType: "dialog",
                                }),
                              })
                            }
                          >
                            <TableCell>{grade.class ? grade.class.name : <>(blank)</>}</TableCell>
                            <TableCell align="right">
                              {grade.grade ? (
                                <Typography color={!grade.grade?.passing ? theme.palette.error.main : undefined}>
                                  {grade.grade.name}
                                </Typography>
                              ) : (
                                <Typography color={theme.palette.warning.main}>{grade.reasonMissing?.name}</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
                footer={
                  gradeTerm.gpa && (
                    <Stack direction="row" spacing="sm" justifyContent={"flex-end"}>
                      <Box>GPA:</Box>
                      <Typography variant="inherit" fontWeight="medium">
                        {formatDecimal(gradeTerm.gpa)}
                      </Typography>
                    </Stack>
                  )
                }
              />
            </Grid>
          ))}
        </Grid>
      </Card>

      {matchTemplatePath(studentGradePageRoute.templatePath) && (
        <ListContext.Provider value={{ loadList: () => props.onGetList() }}>
          <Outlet />
        </ListContext.Provider>
      )}

      {matchTemplatePath(studentGradeBulkCreatePageRoute.templatePath) && (
        <ListContext.Provider value={{ loadList: () => props.onGetList() }}>
          <Outlet />
        </ListContext.Provider>
      )}
    </>
  );
};
